/**
 * Developer VE - List View (default) with client-side pivot to Matrix
 *
 * Fetches from /developer/ve_list/ which returns:
 *   [{suggestion: {...}, prices: [{...}]}]
 *
 * List view: shows suggestion + aggregated savings column
 * Matrix view: pivots prices by company_id into columns (same as old veDeveloper.tsx)
 */
import {useAuthInfo} from "@propelauth/react";
import {useParams} from "react-router-dom";
import React, {useContext, useEffect, useState} from "react";

import {VEListItem, VEPricing, VESuggestion} from "./ve_model";
import {CardHeader, CardTitle} from "../ui/card";
import {Separator} from "../ui/separator";
import {Button} from "../ui/button";
import {ArrowUpDown} from "lucide-react";

import {
    Pencil1Icon,
    Share1Icon,
    CheckCircledIcon,
    CheckIcon,
} from "@radix-ui/react-icons";
import {CompanyBase} from "@models/company";
import {fetchWithToken, putWithToken} from "../../api/apiBase";
import {SortableHeader} from "../ui/tableUtils";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "../ui/tooltip";
import {useToast} from "../ui/use-toast";
import ScrollTable from "../ui/ScrollableTable";
import {moneyFormatter} from "../../lib/formatters";
import {CopyButton2} from "../exportData/CopyButton";
import {ProjectContext} from "../../contexts/ProjectContext";
import {GetScope, ScopeMultiSelect} from "../../components/scope/ScopeSelector";
import {Badge} from "../../components/ui/badge";
import {DeveloperVeItemForm} from "./developer/DeveloperVeItemForm";


type CompanyColumnProps = {
    company: { name: string; _id: string };
    column: any;
};

function CompanyColumn({company, column}: CompanyColumnProps) {
    return (
        <Button
            className="text-wrap text-xs"
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
            {company.name}
            <ArrowUpDown className="ml-2 h-4 w-4"/>
        </Button>
    );
}

/** Pivot list data into matrix format */
function pivotToMatrix(items: any[]) {
    const companyMap: Record<string, any> = {};
    for (const item of items) {
        for (const price of (item.prices || [])) {
            const cid = price.company_id;
            if (cid && !companyMap[cid]) {
                companyMap[cid] = {
                    company_id: cid,
                    company: price.created_by?.company || {name: "Unknown", _id: cid},
                };
            }
        }
    }
    const columns = Object.values(companyMap);
    const emptyRow: Record<string, any> = {};
    for (const col of columns) {
        emptyRow[col.company_id] = {savings_amount: null};
    }

    const rows = items.map((item) => {
        const row: any = {...emptyRow, suggestion: item.suggestion};
        for (const price of (item.prices || [])) {
            if (price.company_id) {
                row[price.company_id] = price;
            }
        }
        return row;
    });
    return {columns, rows};
}

function getScopeName(scopes: any[], scope_id: string | null) {
    if (!scope_id) return '';
    return scopes?.find((x: any) => x._id === scope_id)?.title || '';
}

function getSharedState(s: any): string {
    if (s.shared_state?.share_enabled === 'allowed') return 'Shared';
    if (s.shared_state?.share_requested) return 'Requested';
    return 'Not Shared';
}

/** Format list view data for Excel export */
function formatListForExport(items: any[], scopes: any[]): any[] {
    return items.map((item) => {
        const s = item.suggestion || {};
        const prices = item.prices || [];
        const best = prices.length > 0
            ? Math.max(...prices.map((p: any) => p.savings_amount || 0))
            : 0;
        return {
            'Suggestion': s.title || '',
            'Description': s.description || '',
            'Scope': getScopeName(scopes, s.scope_id),
            'Suggested By': s.created_by?.company?.name || '',
            'Shared': getSharedState(s),
            'Created': s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
            'Best Savings': best,
            '# Prices': prices.length,
        };
    });
}

/** Format matrix view data for Excel export */
function formatMatrixForExport(items: any[], scopes: any[]): any[] {
    const mat = pivotToMatrix(items);
    return mat.rows.map((row) => {
        const s = row.suggestion || {};
        const base: any = {
            'Suggestion': s.title || '',
            'Description': s.description || '',
            'Scope': getScopeName(scopes, s.scope_id),
            'Suggested By': s.created_by?.company?.name || '',
            'Shared': getSharedState(s),
            'Created': s.created_at ? new Date(s.created_at).toLocaleDateString() : '',
        };
        for (const col of mat.columns) {
            const price = row[col.company_id];
            base[col.company?.name || col.company_id] = price?.savings_amount || '';
        }
        return base;
    });
}


function DeveloperVeListView() {
    const {project_id} = useParams<string>();
    const {accessToken} = useAuthInfo();
    const projectContext = useContext(ProjectContext);
    const {toast} = useToast();

    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [suggestionId, setSuggestionId] = React.useState<string | null>(null);
    const [priceId, setPriceId] = React.useState<string | null>(null);
    const [editingItem, setEditingItem] = React.useState<VEListItem | null>(null);
    const [scopeId, setScopeId] = React.useState<string[]>([]);
    const [showDetail, setShowDetail] = React.useState<boolean>(false);

    const [viewMode, setViewMode] = React.useState<'list' | 'matrix'>('list');
    const [items, setItems] = React.useState<any[] | null>(null);
    const [tableCols, setTableColumns] = useState<any[]>([]);
    const [tableRows, setTableRows] = useState<any[]>([]);

    // -----------------------------------------------
    // Fetch list data
    // -----------------------------------------------
    useEffect(() => {
        if (project_id && accessToken) {
            let url = `/projects/${project_id}/developer/ve_list/`;
            if (scopeId && scopeId.length > 0) {
                const params = new URLSearchParams();
                scopeId.forEach(s => params.append('scope_ids', s));
                url += `?${params.toString()}`;
            }
            fetchWithToken(url, accessToken)
                .then((data) => {
                    setItems(data);
                })
                .catch((err) => {
                    console.log("Error:", err);
                    toast({
                        title: 'Error',
                        description: `Failed to Load VEs: ${err}`,
                        variant: 'destructive',
                    });
                });
        }
    }, [project_id, scopeId]);

    // -----------------------------------------------
    // Build table cols/rows whenever items or viewMode change
    // -----------------------------------------------
    useEffect(() => {
        if (!items) return;
        if (viewMode === 'list') {
            setTableColumns(buildListCols());
            setTableRows(items);
        } else {
            const mat = pivotToMatrix(items);
            setTableColumns(buildMatrixCols(mat.columns));
            setTableRows(mat.rows);
        }
    }, [items, viewMode]);

    // -----------------------------------------------
    // Form open/close
    // -----------------------------------------------
    const onSuggestVE = (e: any) => {
        e.preventDefault();
        onCloseVE();
        setIsEditing(true);
    };

    const onCloseVE = () => {
        setIsEditing(false);
        setSuggestionId(null);
        setEditingItem(null);
        setPriceId(null);
    };

    const onOpenItem = (suggestion: VESuggestion, price: any) => {
        console.log(suggestion);
        setSuggestionId(suggestion.id);
        if (price) {
            setPriceId(price._id);
            setEditingItem({price: {...price, id: price._id}, suggestion: suggestion});
        } else {
            setEditingItem({price: null, suggestion: suggestion});
        }
        setIsEditing(true);
    };

    // -----------------------------------------------
    // State update helpers (operate on the single items array)
    // -----------------------------------------------
    const shareSuggestion = (suggestion_id: string) => {
        if (accessToken) {
            putWithToken(
                `/projects/${project_id}/ve/${suggestion_id}/share`,
                null,
                accessToken)
                .then((res: VESuggestion) => {
                    setItems((prev) => {
                        if (!prev) return prev;
                        return prev.map((item) =>
                            item.suggestion?.id === suggestion_id
                                ? {...item, suggestion: res}
                                : item
                        );
                    });
                    toast({
                        title: "Success!",
                        description: "VE item shared.",
                        variant: "default",
                    });
                }).catch(err => {
                    toast({variant: "destructive", title: "Error", description: err || "Could not share"});
                });
        }
    };

    const insertItem = (row: VEListItem) => {
        if (!items) return;
        setItems([{suggestion: row.suggestion, prices: []}, ...items]);
        setSuggestionId(null);
        setEditingItem(null);
    };

    const hideItem = (row: VEListItem) => {
        if (!items) return;
        setItems(items.filter((item) => item.suggestion.id !== row.suggestion.id));
        onCloseVE();
    };

    const updateItem = (row: VEListItem) => {
        if (!items) return;
        setItems(items.map((item) =>
            item.suggestion.id === row.suggestion.id
                ? {...item, suggestion: row.suggestion}
                : item
        ));
    };

    const handleScopeSelect = (scope: any[]) => {
        setScopeId(scope);
    };

    // -----------------------------------------------
    // Shared action cell (used by both list and matrix cols)
    // -----------------------------------------------
    const actionCell = (row: any) => {
        return (
            <div className="flex">
                <Button
                    variant="outline" className="mr-5"
                    size="sm"
                    onClick={() => onOpenItem(row.original.suggestion, null)}
                >
                    <Pencil1Icon className="h-4 w-4"/>
                </Button>

                {row.original.suggestion.shared_state?.share_enabled === "allowed"
                    ? (<CheckCircledIcon className="mr-5" width={17} height={17}/>)
                    : (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <a className="mr-5"
                                       onClick={() => shareSuggestion(row.original.suggestion.id)}>
                                        <Share1Icon width={17} height={17}/>
                                    </a>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Share VE idea with other bidders</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}

                <Badge variant={"outline"}>
                    {GetScope(projectContext, row.original.suggestion.scope_id)?.title || ""}
                </Badge>
            </div>
        );
    };

    const baseCols = (): any[] => [
        {
            id: "Actions",
            header: "",
            cell: actionCell,
        },
        {
            header: ({column}: any) => (<SortableHeader column={column} name={"Suggestion"}/>),
            accessorKey: "suggestion.title",
        },
        {
            header: ({column}: any) => (<SortableHeader column={column} name={"Description"}/>),
            accessorKey: "suggestion.description",
        },

        {
            header: ({column}: any) => (<SortableHeader column={column} name={"Created"}/>),
            accessorKey: "suggestion.created_at",
            cell: (row: any) => {
                if (row?.original?.suggestion?.created_at) {
                    const date = new Date(row.original.suggestion.created_at);
                    return <span>{date.toLocaleDateString()}</span>;
                }
                return <></>;
            },
        },
         {
            header: ({column}: any) => (<SortableHeader column={column} name={"Suggested By"}/>),
            id: "suggested_by",
            cell: (row: any) => {
                const cb = row.original.suggestion?.created_by;
                return <span>{cb?.company?.name || ""}</span>;
            },
        },
    ];

    // -----------------------------------------------
    // List columns: base + savings summary
    // -----------------------------------------------
    const buildListCols = () => {
        return [
            ...baseCols(),
            {
                header: ({column}: any) => (<SortableHeader column={column} name={"Savings"}/>),
                id: "savings",
                cell: (row: any) => {
                    const prices = row.original.prices || [];
                    if (prices.length === 0) return <span className="text-muted-foreground">-</span>;
                    const best = Math.max(...prices.map((p: any) => p.savings_amount || 0));
                    return (
                        <span>
                            {moneyFormatter.format(best)}
                            <span className="text-muted-foreground ml-2">
                                ({prices.length} {prices.length === 1 ? "price" : "prices"})
                            </span>
                        </span>
                    );
                },
            },
        ];
    };

    // -----------------------------------------------
    // Matrix columns: base + per-company pivot columns
    // -----------------------------------------------
    const buildMatrixCols = (columns: any[]) => {
        const arr = baseCols();
        for (const col of columns) {
            const company_key = col.company_id;
            arr.push({
                key: company_key,
                accessorKey: company_key + ".savings_amount",
                header: ({column}: any) => (
                    <CompanyColumn column={column} company={col.company}/>
                ),
                cell: (row: any) => {
                    if (!row.original[company_key]) return <></>;
                    const amount = row.original[company_key]["savings_amount"];
                    if (!amount) return <></>;

                    const price_item = row.original[company_key];
                    const op = row.original.suggestion.created_by?.company?.id;

                    return (
                        <>
                            <button className="flex"
                                    onClick={() => onOpenItem(row.original.suggestion, price_item)}>
                                <span className="mr-3 hover:underline">{moneyFormatter.format(amount)}</span>
                                {op === company_key &&
                                    <CheckIcon className="mr-5" width={17} height={17}/>}
                            </button>
                            {showDetail && <div>{price_item.description}</div>}
                        </>
                    );
                },
            });
        }
        return arr;
    };

    const formatDataToExport = () => {
        if (!items) return [];
        const scopes = projectContext?.scopes || [];
        return viewMode === 'list'
            ? formatListForExport(items, scopes)
            : formatMatrixForExport(items, scopes);
    };

    return (
        <div className="container max-w-full overflow-x-hidden">
            <div className={`flex ${isEditing ? "flex-row" : "flex-col"}`}>
                <div className={` ${isEditing ? "w-2/3" : "w-full"} `}>
                    <br/>
                    <CardHeader>
                        <CardTitle>
                            <div className="flex justify-between">

                                <div className="flex gap-2">
                                    <ScopeMultiSelect
                                        onChange={handleScopeSelect}
                                        selected={scopeId}/>
                                    <Button variant="outline"
                                            onClick={onSuggestVE}>
                                        Suggest VE
                                    </Button>
                                    <CopyButton2 dataTransform={formatDataToExport}/>
                                    <Separator orientation="vertical" className="h-8" />
                                    <div className="flex gap-1 border rounded-md">
                                        <Button
                                            variant={viewMode === 'list' ? 'default' : 'ghost'}
                                            size="sm"
                                            onClick={() => setViewMode('list')}>
                                            List
                                        </Button>
                                        <Button
                                            variant={viewMode === 'matrix' ? 'default' : 'ghost'}
                                            size="sm"
                                            onClick={() => setViewMode('matrix')}>
                                            Matrix
                                        </Button>
                                    </div>


                                </div>
                                  <div>Value Engineering</div>
                            </div>
                        </CardTitle>
                    </CardHeader>
                    {tableRows.length > 0 &&
                        <div className="max-w-full text-sm">
                            <ScrollTable
                                columns={tableCols}
                                data={tableRows}
                                maxHeight="h-[calc(100vh-250px)]"/>
                        </div>
                    }
                </div>
                {isEditing && (
                    <div className="w-1/3 ml-4">
                        <div className="flex justify-end mb-[20px]">
                            <Button onClick={onCloseVE}>Close</Button>
                        </div>
                        <DeveloperVeItemForm
                            suggestion_id={suggestionId}
                            price_id={priceId}
                            setItemId={setPriceId}
                            setSuggestionId={setSuggestionId}
                            suggestionPrice={editingItem}
                            updateItem={updateItem}
                            insertItem={insertItem}
                            hideItem={hideItem}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default DeveloperVeListView;
